import { messageConstants } from "../actions/constants";

const initialState = {
    messages: [],
    loading: false,
    sending: false,
    sendingProgress: 0,
    sendError: null,
    forwarding: false,
    deletingById: {},
    error: null,
    nextCursorByChat: {},
    hasMoreByChat: {},
    loadingOlderByChat: {},
};

const getId = (value) => String(value?._id || value || "");

const normalizeReadBy = (readBy = []) => readBy.map((item) => getId(item)).filter(Boolean);

const sortMessages = (messages = []) => messages.slice().sort((a, b) => (
    new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
));

const mergeMessages = (base = [], incoming = []) => {
    const map = new Map();

    base.forEach((message) => {
        if (message?._id) map.set(String(message._id), message);
    });

    incoming.forEach((message) => {
        if (!message?._id) return;
        const existing = map.get(String(message._id));
        map.set(String(message._id), existing ? { ...existing, ...message } : message);
    });

    return sortMessages(Array.from(map.values()));
};

const addMessage = (messages, message) => (
    message?._id ? mergeMessages(messages, [message]) : messages
);

const getReplyMessageId = (message) => (
    getId(message?.replyTo?.messageId)
);

/**
 * Отправленное своё фото не мигает: пока не скачалась копия с сервера,
 * показываем то, что уже было на экране (файл с компьютера).
 */
const keepLocalPreview = (message, local) => (
    message && local?.__localUrl
        ? { ...message, __localUrl: local.__localUrl, __clientMessageId: local.__clientMessageId }
        : message
);

const findMatchingOptimisticIndex = (messages, incoming, meId) => {
    if (!incoming) return -1;

    // Надёжнее всего — по номеру сообщения, который придумал браузер.
    const clientMessageId = String(incoming.clientMessageId || "");
    if (clientMessageId) {
        const byClientId = messages.findIndex((item) => (
            String(item?.__clientMessageId || "") === clientMessageId
        ));
        if (byClientId >= 0) return byClientId;
    }

    // Старый сервер номер не возвращает — ищем текст без файла с тем же содержимым.
    if (!meId || incoming.fileName || incoming.fileType) return -1;
    if (getId(incoming.sender) !== getId(meId)) return -1;

    const incomingChatId = getId(incoming.chatId);
    const incomingText = String(incoming.text || "");
    const incomingReplyId = getReplyMessageId(incoming);

    return messages.findIndex((item) => (
        item?.__sendStatus === "sending" &&
        !item?.fileName &&
        getId(item.chatId) === incomingChatId &&
        String(item.text || "") === incomingText &&
        getReplyMessageId(item) === incomingReplyId
    ));
};

export default function messageReducer(state = initialState, action) {
    switch (action.type) {
        case messageConstants.GET_MESSAGES_REQUEST:
            return {
                ...state,
                loading: true,
                error: null,
            };

        case messageConstants.GET_MESSAGES_OLDER_REQUEST: {
            const chatId = getId(action.payload?.chatId);
            return {
                ...state,
                error: null,
                loadingOlderByChat: {
                    ...state.loadingOlderByChat,
                    [chatId]: true,
                },
            };
        }

        case messageConstants.GET_MESSAGES_SUCCESS: {
            const chatId = getId(action.payload?.chatId);
            const incoming = action.payload?.messages || [];
            const append = !!action.payload?.append;
            const nextCursor = action.payload?.nextCursor || null;
            const hasMore = !!action.payload?.hasMore;

            if (!chatId) {
                return {
                    ...state,
                    messages: sortMessages(incoming),
                    loading: false,
                    error: null,
                };
            }

            const otherMessages = state.messages.filter((message) => getId(message.chatId) !== chatId);
            const currentMessages = state.messages.filter((message) => getId(message.chatId) === chatId);
            const localPending = currentMessages.filter((message) => (
                message?.__sendStatus === "sending" ||
                message?.__sendStatus === "failed"
            ));
            const nextChatMessages = append
                ? mergeMessages(currentMessages, incoming)
                : mergeMessages(incoming, localPending);

            return {
                ...state,
                messages: sortMessages([...otherMessages, ...nextChatMessages]),
                loading: false,
                error: null,
                nextCursorByChat: {
                    ...state.nextCursorByChat,
                    [chatId]: nextCursor,
                },
                hasMoreByChat: {
                    ...state.hasMoreByChat,
                    [chatId]: hasMore,
                },
                loadingOlderByChat: {
                    ...state.loadingOlderByChat,
                    [chatId]: false,
                },
            };
        }

        case messageConstants.GET_MESSAGES_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload?.error || "Ошибка загрузки сообщений",
            };

        case messageConstants.GET_MESSAGES_OLDER_FAILURE: {
            const chatId = getId(action.payload?.chatId);
            return {
                ...state,
                error: action.payload?.error || "Ошибка загрузки истории",
                loadingOlderByChat: {
                    ...state.loadingOlderByChat,
                    [chatId]: false,
                },
            };
        }

        case messageConstants.SEND_MESSAGE_OPTIMISTIC_ADD:
            return {
                ...state,
                messages: addMessage(state.messages, action.payload?.message),
                sendError: null,
            };

        case messageConstants.SEND_MESSAGE_OPTIMISTIC_CONFIRM: {
            const clientMessageId = String(action.payload?.clientMessageId || "");
            const confirmedMessage = action.payload?.message || null;
            const local = state.messages.find((message) => (
                String(message?.__clientMessageId || "") === clientMessageId
            ));
            const withoutLocal = state.messages.filter((message) => (
                String(message?.__clientMessageId || "") !== clientMessageId
            ));

            return {
                ...state,
                messages: confirmedMessage
                    ? addMessage(withoutLocal, keepLocalPreview(confirmedMessage, local))
                    : withoutLocal,
                sendError: null,
            };
        }

        case "SEND_MESSAGE_OPTIMISTIC_PROGRESS": {
            const clientMessageId = String(action.payload?.clientMessageId || "");
            const progress = Math.min(100, Math.max(0, Number(action.payload?.progress || 0)));

            return {
                ...state,
                messages: state.messages.map((message) => (
                    String(message?.__clientMessageId || "") === clientMessageId &&
                    message.__sendStatus === "sending"
                        ? { ...message, __uploadProgress: progress }
                        : message
                )),
            };
        }

        case "SEND_MESSAGE_OPTIMISTIC_RETRY": {
            const clientMessageId = String(action.payload?.clientMessageId || "");

            return {
                ...state,
                messages: state.messages.map((message) => (
                    String(message?.__clientMessageId || "") === clientMessageId
                        ? { ...message, __optimistic: true, __sendStatus: "sending", __sendError: "", __uploadProgress: 0 }
                        : message
                )),
                sendError: null,
            };
        }

        case "SEND_MESSAGE_OPTIMISTIC_DISCARD": {
            const clientMessageId = String(action.payload?.clientMessageId || "");
            const local = state.messages.find((message) => (
                String(message?.__clientMessageId || "") === clientMessageId
            ));
            if (local?.__localUrl && typeof URL !== "undefined") {
                try { URL.revokeObjectURL(local.__localUrl); } catch (_error) { /* уже освобождён */ }
            }

            return {
                ...state,
                messages: state.messages.filter((message) => (
                    String(message?.__clientMessageId || "") !== clientMessageId
                )),
                sendError: null,
            };
        }

        case messageConstants.SEND_MESSAGE_OPTIMISTIC_FAILURE: {
            const clientMessageId = String(action.payload?.clientMessageId || "");
            const error = action.payload?.error || "Не удалось отправить сообщение";

            return {
                ...state,
                messages: state.messages.map((message) => (
                    String(message?.__clientMessageId || "") === clientMessageId
                        ? {
                            ...message,
                            __optimistic: false,
                            __sendStatus: "failed",
                            __sendError: error,
                        }
                        : message
                )),
                sendError: error,
            };
        }

        case messageConstants.SEND_MESSAGE_REQUEST:
            return {
                ...state,
                sending: true,
                sendingProgress: 0,
                sendError: null,
            };

        case messageConstants.SEND_MESSAGE_PROGRESS:
            return {
                ...state,
                sendingProgress: Math.min(100, Math.max(0, Number(action.payload?.progress || 0))),
            };

        case messageConstants.SEND_MESSAGE_SUCCESS:
        case messageConstants.SEND_TEXT_SUCCESS:
        case messageConstants.SEND_FILE_SUCCESS:
        case messageConstants.SEND_VOICE_SUCCESS:
            return {
                ...state,
                messages: addMessage(state.messages, action.payload?.message),
                sending: false,
                sendingProgress: 100,
                sendError: null,
            };

        case messageConstants.SEND_MESSAGE_FAILURE:
            return {
                ...state,
                sending: false,
                sendingProgress: 0,
                sendError: action.payload?.error || "Не удалось отправить сообщение",
            };

        case messageConstants.FORWARD_MESSAGE_REQUEST:
            return {
                ...state,
                forwarding: true,
                sendError: null,
            };

        case messageConstants.FORWARD_MESSAGE_SUCCESS: {
            const forwardedMessages = Array.isArray(action.payload?.messages)
                ? action.payload.messages
                : (action.payload?.message ? [action.payload.message] : []);

            return {
                ...state,
                messages: mergeMessages(state.messages, forwardedMessages),
                forwarding: false,
                sendError: null,
            };
        }

        case messageConstants.FORWARD_MESSAGE_FAILURE:
            return {
                ...state,
                forwarding: false,
                sendError: action.payload?.error || "Не удалось переслать сообщение",
            };

        case messageConstants.RECEIVE_NEW_MESSAGE: {
            const message = action.payload?.message;

            if (message?.__starPatch) {
                return {
                    ...state,
                    messages: state.messages.map((item) => (
                        String(item._id) === String(message._id)
                            ? { ...item, starredBy: message.starredBy || item.starredBy || [] }
                            : item
                    )),
                };
            }

            const optimisticIndex = findMatchingOptimisticIndex(
                state.messages,
                message,
                action.payload?.meId
            );

            if (optimisticIndex >= 0) {
                const nextMessages = state.messages.slice();
                const [local] = nextMessages.splice(optimisticIndex, 1);

                return {
                    ...state,
                    messages: addMessage(nextMessages, keepLocalPreview(message, local)),
                };
            }

            return {
                ...state,
                messages: addMessage(state.messages, message),
            };
        }

        case 'MESSAGE_UPDATED_RECEIVED': {
            const updated = action.payload?.message;
            const id = getId(updated?._id);
            if (!id) return state;
            let found = false;
            const messages = state.messages.map((message) => {
                if (getId(message._id) !== id) return message;
                found = true;
                // Server might send only changed fields; preserve other fields in state.
                const merged = { ...message, ...updated };
                if (updated.voiceId && message.voiceId && typeof updated.voiceId === 'object') {
                    merged.voiceId = { ...message.voiceId, ...updated.voiceId };
                }
                return merged;
            });
            return found ? { ...state, messages } : state;
        }

        case 'MESSAGE_REACTIONS_PATCHED': {
            const id = getId(action.payload?.messageId);
            if (!id) return state;
            return {
                ...state,
                messages: state.messages.map((message) => getId(message?._id) === id
                    ? { ...message, reactions: action.payload?.reactions || [] }
                    : message),
            };
        }

        case messageConstants.DELETE_MESSAGE_REQUEST: {
            const messageId = getId(action.payload?.messageId);
            return {
                ...state,
                deletingById: {
                    ...state.deletingById,
                    [messageId]: true,
                },
            };
        }

        case messageConstants.DELETE_MESSAGE_SUCCESS: {
            const messageId = getId(action.payload?.messageId);
            const nextDeleting = { ...state.deletingById };
            delete nextDeleting[messageId];

            return {
                ...state,
                messages: state.messages.filter((message) => String(message._id) !== messageId),
                deletingById: nextDeleting,
            };
        }

        case messageConstants.DELETE_MESSAGE_FAILURE: {
            const messageId = getId(action.payload?.messageId);
            const nextDeleting = { ...state.deletingById };
            delete nextDeleting[messageId];

            return {
                ...state,
                deletingById: nextDeleting,
                error: action.payload?.error || "Ошибка удаления сообщения",
            };
        }

        case messageConstants.CLEAR_MESSAGES:
            return { ...initialState };

        case messageConstants.MARK_MESSAGES_AS_READ_SUCCESS: {
            const chatId = getId(action.payload?.chatId);
            const userId = getId(action.payload?.userId);

            if (!chatId || !userId) return state;

            return {
                ...state,
                messages: state.messages.map((message) => {
                    const messageChatId = getId(message.chatId);
                    const senderId = getId(message.sender);

                    if (messageChatId !== chatId || senderId === userId) return message;

                    const readByIds = normalizeReadBy(message.readBy);
                    if (readByIds.includes(userId)) return message;

                    return {
                        ...message,
                        readBy: [...(message.readBy || []), userId],
                    };
                }),
            };
        }

        default:
            return state;
    }
}
